import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import { DbFileInfos } from "../../../Core/Api/Types/db-file-infos";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import { bytesToKB } from "../../Misc/DropFileZone/drop-file-zone";
import moment from "moment";
import PublicIcon from "@mui/icons-material/Public";
import SpeedIcon from "@mui/icons-material/Speed";
import DetailsIcon from "@mui/icons-material/Details";
import StorageIcon from "@mui/icons-material/Storage";
import FeaturedPlayListIcon from "@mui/icons-material/FeaturedPlayList";
import { mediaService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormSection from "../../Forms/FormSection/form-section";
import CodeBox from "../../Misc/CodeBox/code-box";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";

export type ModalFileDetailsElementProps = {
  onClose?: (props: any) => void;
  file: DbFileInfos;
};

export type ModalFileDetailsProps = CommonModalProps<ModalFileDetailsElementProps>;

const ModalFileDetails = React.forwardRef<any, ModalFileDetailsProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onClose, ...elementProps } = props;
  const { file } = elementProps;
  const customSnackbar = useCustomSnackbar();

  const handleParseMedia = () => {
    mediaService.ParseMediaFile(file.id).then((response) => {
      if (response && response.success) {
        customSnackbar(t("alert:success.parsingMediaDone"), "success");
        closeModal();
      } else customSnackbar(t("alert:warning.errorParsingMediaFile", { message: response.message }), "warning");
    });
  };
  const handleSyncMedia = () => {
    mediaService.SyncMediaFileMetadata(file.id).then(function () {
      customSnackbar(t("alert:success.synchronizingMediaDone"), "success");
    });
  };

  return (
    <Modal
      title={t("modal:title.fileDetailsWithName", { fileName: file.name })}
      closeModal={closeModal}
      width={1000}
      reference={ref}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button disabled={!UserHasAcl(AclTypes.aclSuperUser)} onClick={handleParseMedia}>
            {t("button:parseMediaAgain")}
          </Button>
          <Button disabled={!UserHasAcl(AclTypes.aclSuperUser)} onClick={handleSyncMedia}>
            {t("button:syncObjectMetadata")}
          </Button>
          <Button variant="outlined" onClick={closeModal}>
            {t("common:close")}
          </Button>
        </Stack>
      }
    >
      <Box>
        <FormSection
          defaultExpanded={true}
          title={t("form:section.title.globalFileInfos")}
          icon={<PublicIcon />}
          sx={{ mb: 2 }}
        >
          <Stack spacing={1}>
            <MetadataListTextItem title="Id">{file.id}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:fileName")}>{file.name}</MetadataListTextItem>
            <MetadataListTextItem title={t("menu:item.fullPath")}>{file.fullPath}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:status")}>{file.status}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:category")}>{file.category}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:accessibility")}>{file.accessibility}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:size")}>
              {bytesToKB(file.sizeInBytes as number) + " KB"}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("common:version")}>{file.version}</MetadataListTextItem>
            <MetadataListTextItem title={t("menu:item.insertOrCreationDate")}>
              {moment(file.creationInfos?.date).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("menu:item.lastUseDate")}>
              {moment(file.usedAt).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("menu:item.isVirtual")}>{String(file.isVirtual)}</MetadataListTextItem>
          </Stack>
        </FormSection>

        <FormSection
          defaultExpanded={true}
          title={t("form:section.title.quickFileInfos")}
          icon={<SpeedIcon />}
          sx={{ mb: 2 }}
        >
          <Stack spacing={1}>
            <MetadataListTextItem title={t("common:timecodeIn")}>
              {file.properties.timecode !== undefined && file.properties.timecode !== null
                ? file.properties.timecode.tcIn
                : "-"}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("common:timecodeOut")}>
              {file.properties.timecode !== undefined && file.properties.timecode !== null
                ? file.properties.timecode.tcOut
                : "-"}
            </MetadataListTextItem>
          </Stack>
        </FormSection>

        <FormSection
          defaultExpanded={true}
          title={t("form:section.title.fileFormatDetails")}
          icon={<DetailsIcon />}
          sx={{ mb: 2 }}
        >
          <Stack spacing={1}>
            <MetadataListTextItem title="Id">{"#" + file.formatInfos?.id}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:name")}>{file.formatInfos?.name}</MetadataListTextItem>
            <Typography color="text.secondary" component="span">
              {t("menu:item.specificationsAsJson")}
            </Typography>
            <CodeBox text={file.formatInfos?.specifications} />
          </Stack>
        </FormSection>

        <FormSection
          defaultExpanded={true}
          title={t("form:section.title.fileStorageLocationDetails")}
          icon={<StorageIcon />}
          sx={{ mb: 2 }}
        >
          <Stack spacing={1}>
            <MetadataListTextItem title="Id">{"#" + file.storageInfos?.id}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:name")}>{file.storageInfos?.name}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:type")}>{file.storageInfos?.type}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:kind")}>{file.storageInfos?.kind}</MetadataListTextItem>
          </Stack>
        </FormSection>

        <FormSection
          defaultExpanded={true}
          title={t("form:section.title.filePropertiesDetails")}
          icon={<FeaturedPlayListIcon />}
        >
          <Stack spacing={1}>
            <MetadataListTextItem title={t("common:tapes")}>
              {file.properties.fileInfos?.tapeIds !== null
                ? file.properties.fileInfos?.tapeIds.map((element) => <Chip label={element} />)
                : t("menu:item.noTapesLinked")}
            </MetadataListTextItem>
            <Typography color="text.secondary" component="span">
              {t("menu:item.propertiesAsJson")}
            </Typography>
            <CodeBox text={file.properties} />
          </Stack>
        </FormSection>
      </Box>
    </Modal>
  );
});

export default ModalFileDetails;
